import React from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Select, MenuItem, FormControl, InputLabel, Button } from "@material-ui/core";
import { css } from "@emotion/css";
import { useQuestionnaireByName, usePublishedQuestionnaireHistory } from "./queries";
import type { PublishedQuestionnaireHistoryItem } from "./api";
import { parseRef, refKey } from "./useLocaleDiffPreviews";
import type { SnapshotRef } from "./useLocaleDiffPreviews";
import DiffShell from "./components/DiffShell";

const refLabel = (r: SnapshotRef, history: PublishedQuestionnaireHistoryItem[], draftExists: boolean): string => {
  if (r.kind === "draft") return draftExists ? "Draft" : "Draft (missing)";
  const item = history.find(h => h.id === r.id);
  if (!item) return `Published #${r.id}`;
  const ts = new Date(item.published_at).toLocaleString();
  return `${ts}${item.is_active ? " (active)" : ""}`;
};

const Compare: React.VFC = () => {
  const { name: rawName } = useParams<{ name: string }>();
  const name = decodeURIComponent(rawName);
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const draftQuery = useQuestionnaireByName(name);
  const historyQuery = usePublishedQuestionnaireHistory(name);

  const draft = draftQuery.data ?? null;
  const publishedHistory = historyQuery.data ?? [];
  const activePublished = publishedHistory.find(p => p.is_active) ?? null;
  const latestPublished = publishedHistory[0] ?? null;

  const defaultBefore: SnapshotRef | null = activePublished
    ? { kind: "published", id: activePublished.id }
    : latestPublished
      ? { kind: "published", id: latestPublished.id }
      : null;

  const defaultAfter: SnapshotRef | null = draft
    ? { kind: "draft" }
    : latestPublished
      ? { kind: "published", id: latestPublished.id }
      : null;

  const before = parseRef(params.get("before")) ?? defaultBefore;
  const after = parseRef(params.get("after")) ?? defaultAfter;

  const updateQuery = (next: { before?: SnapshotRef | null; after?: SnapshotRef | null }) => {
    const sp = new URLSearchParams(location.search);
    const apply = (key: "before" | "after", value: SnapshotRef | null | undefined) => {
      if (value === undefined) return;
      if (value === null) sp.delete(key);
      else sp.set(key, refKey(value));
    };
    apply("before", next.before);
    apply("after", next.after);
    history.push({ pathname: location.pathname, search: sp.toString() });
  };

  const snapshotOptions: SnapshotRef[] = [
    ...(draft ? [{ kind: "draft" as const }] : []),
    ...publishedHistory.map(p => ({ kind: "published" as const, id: p.id })),
  ];

  const renderSnapshotSelect = (
    label: string,
    value: SnapshotRef | null,
    onChange: (next: SnapshotRef | null) => void
  ) => (
    <FormControl size="small" variant="outlined" className={styles.snapshotSelect}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value ? refKey(value) : ""}
        label={label}
        onChange={e => onChange(parseRef(String(e.target.value)))}
      >
        {snapshotOptions.map(opt => (
          <MenuItem key={refKey(opt)} value={refKey(opt)}>
            {refLabel(opt, publishedHistory, !!draft)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const actions = (
    <>
      {renderSnapshotSelect("Before", before, next => updateQuery({ before: next }))}
      {renderSnapshotSelect("After", after, next => updateQuery({ after: next }))}
      <Button variant="outlined" onClick={() => history.push("/health-forms/published")}>
        Cancel
      </Button>
    </>
  );

  const isLoading = draftQuery.isLoading || historyQuery.isLoading;
  const error = draftQuery.error || historyQuery.error ? "Failed to load versions for this questionnaire" : null;

  return (
    <DiffShell
      title={`Compare: ${name}`}
      before={before}
      after={after}
      draftId={draft?.id ?? null}
      previousLabel={before ? refLabel(before, publishedHistory, !!draft) : "Before"}
      nextLabel={after ? refLabel(after, publishedHistory, !!draft) : "After"}
      emptyMessage="Nothing to compare — this name has no draft or published versions yet."
      actions={actions}
      isLoading={isLoading}
      error={error}
    />
  );
};

export default Compare;

const styles = {
  snapshotSelect: css`
    min-width: 220px;
  `,
};
